// @ts-strict-ignore
import type { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import { trackEvent } from '@/analytics/trackEvent';
import { CONNECT_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import { useRefetchBotCategories } from '@/components/ChatBot/hooks';
import { useRefetchAllWebsites } from '@/features/Connect/hooks/useFetchAllWebsites';
import { useRefetchWebsite } from '@/features/Connect/hooks/useFetchWebsite';
import { QUERY_KEY } from '@/features/Connect/hooks/useSearchWebsites';
import { useInvalidateAllFeedDetails } from '@/features/CustomFeedV2/hooks';
import { useRefetchCategoryTabs } from '@/hooks/query/useFetchCategoryTabs';
import { useInvalidateFetchFeeds } from '@/hooks/query/useFetchFeeds';
import { useInvalidatePrivateApps } from '@/hooks/query/useFetchPrivateApps';
import { useInvalidatePublicApps } from '@/hooks/query/useFetchPublicApps';
import { useRefetchUserStatus } from '@/hooks/query/useUserStatus';
import type { BundleSourceType } from '@/types';

import { useRefetchAppLibraryData } from './appLibraryData';
import type { AppLibraryDataResponse, AppTileType } from './types';

const usePublicNavigationRefetch = (): (() => void) => {
  const refetchUserStatus = useRefetchUserStatus();
  const refetchAppLibraryData = useRefetchAppLibraryData();
  const refetchPublicApps = useInvalidatePublicApps();
  const refetchAllBoardsDetails = useInvalidateAllFeedDetails();
  const refetchCategoryTabs = useRefetchCategoryTabs();
  const refetchBotCategories = useRefetchBotCategories();

  return () => {
    refetchAppLibraryData();
    refetchUserStatus();
    refetchPublicApps();
    refetchAllBoardsDetails();
    refetchCategoryTabs();
    refetchBotCategories();
  };
};

// Cleanup when old feedly results code is removed
const usePublicSourceCustomIntegrationMutation = () => {
  const queryClient = useQueryClient();
  const refetch = usePublicNavigationRefetch();
  const refetchAllWebsites = useRefetchAllWebsites();

  return useMutation(
    (source: BundleSourceType) =>
      axiosInstance.post<never>(CONNECT_API_ROUTES.custom('public'), {
        postdata: source,
      }),
    {
      onSettled: () => {
        refetch();
        refetchAllWebsites();
      },
      onMutate: (source: BundleSourceType) => {
        const previousData = queryClient.getQueryData<
          AxiosResponse<AppLibraryDataResponse>
        >([QUERY_KEY, source.topic]);

        queryClient.setQueryData<AxiosResponse<AppLibraryDataResponse>>(
          [QUERY_KEY, source.topic],
          (previousData) => {
            if (!previousData || !source.topic) return previousData;

            return {
              ...previousData,
              data: {
                ...previousData.data,
                results: previousData.data.results.map((oldSource) => {
                  if (oldSource.label === source.label) {
                    return {
                      ...oldSource,
                      flag: true,
                    };
                  }
                  return oldSource;
                }),
              },
            };
          }
        );

        return { previousData };
      },
      onError: (
        _,
        source,
        context: { previousData: AxiosResponse<AppLibraryDataResponse> }
      ) => {
        queryClient.setQueryData<AxiosResponse<AppLibraryDataResponse>>(
          [QUERY_KEY, source.topic],
          context.previousData
        );
      },
    }
  );
};

const useFollowWebsite = () => {
  const queryClient = useQueryClient();
  const refetch = usePublicNavigationRefetch();
  const refetchAllWebsites = useRefetchAllWebsites();

  return useMutation(
    (source: BundleSourceType) =>
      axiosInstance.post<never>(CONNECT_API_ROUTES.custom('public'), {
        postdata: source,
      }),
    {
      onSettled: () => {
        refetch();
        refetchAllWebsites();
      },
      onSuccess: (_, { label: sourceId, topic }) => {
        queryClient.setQueryData<AppTileType>(
          [QUERY_KEY, topic, sourceId],
          (previousData) => {
            if (!previousData) return previousData;

            return { ...previousData, flag: true };
          }
        );
      },
    }
  );
};

const useUnfollowWebsite = () => {
  const queryClient = useQueryClient();
  const refetch = usePublicNavigationRefetch();

  return useMutation(
    ({ label }: AppTileType) =>
      axiosInstance.post<never>(
        CONNECT_API_ROUTES.public(),
        { key: label },
        { params: { delink: true } }
      ),
    {
      onSettled: () => {
        refetch();
      },
      onSuccess: (_, { label, topic }) => {
        queryClient.setQueryData<AppTileType>(
          [QUERY_KEY, topic, label],
          (previousData) => {
            if (!previousData) return previousData;

            return { ...previousData, flag: false };
          }
        );
      },
    }
  );
};

const usePublicSourceMultipleIntegrationMutation = () => {
  const refetch = usePublicNavigationRefetch();

  return useMutation(
    (sourceKeys: string[]) =>
      axiosInstance.post<never>(CONNECT_API_ROUTES.multiple(), {
        postdata: sourceKeys.map((key) => ({ key })),
      }),
    {
      onSettled: () => {
        refetch();
      },
    }
  );
};

const usePublicSourceIntegrationMutation = ({
  isWebsite,
  isNeedlWeb,
  isConnectPage,
  isCustomFeed,
}: PublicSourceType) => {
  const refetch = usePublicNavigationRefetch();
  const invalidateWebsiteFetchQuery = useRefetchWebsite();

  return useMutation(
    ({ key }: PublicSourceMutationPayload) =>
      axiosInstance.post<never>(CONNECT_API_ROUTES.public(), { key }),
    {
      onSettled: (_, key: string) => {
        refetch();
        if (isWebsite) invalidateWebsiteFetchQuery(key);
      },
      onSuccess: (_, { key, label }) => {
        const eventName = isCustomFeed
          ? 'website_followed_feeddefinition'
          : `${isWebsite ? 'website' : 'exchanges'}_followed${
              isNeedlWeb ? '_needlweb' : ''
            }`;

        const properties = {
          sourceId: key,
          ...(label && { sourceName: label }),
          origin: isNeedlWeb
            ? 'needl_web'
            : isConnectPage
            ? 'connect_page'
            : isCustomFeed
            ? 'feed_definition'
            : 'shared_data',
        };
        trackEvent(eventName, properties);
      },
    }
  );
};

const usePublicSourceDelinkMutation = () => {
  const refetch = usePublicNavigationRefetch();

  const invalidateQuery = useRefetchWebsite();

  return useMutation(
    (key: string) =>
      axiosInstance.post<never>(
        CONNECT_API_ROUTES.public(),
        { key },
        { params: { delink: true } }
      ),
    {
      onSettled: (_, __, key) => {
        invalidateQuery(key);
      },
      onSuccess: () => {
        refetch();
      },
    }
  );
};

const useFeedTemplateMutation = () => {
  const refetchAppLibraryData = useRefetchAppLibraryData();
  const refetchFeeds = useInvalidateFetchFeeds();
  const refetchPublicApps = useInvalidatePublicApps();
  const refetchPrivateApps = useInvalidatePrivateApps();
  const refetchAllBoardsDetails = useInvalidateAllFeedDetails();
  const refetchCategoryTabs = useRefetchCategoryTabs();
  const refetchBotCategories = useRefetchBotCategories();

  const mutation = useMutation(
    (bundle: string) =>
      axiosInstance.post(CONNECT_API_ROUTES.setFeedTemplates(), {
        bundles: [bundle],
      }),
    {
      onSuccess: () => {
        refetchAppLibraryData();
        refetchFeeds();
        refetchPrivateApps();
        refetchPublicApps();
        refetchAllBoardsDetails();
        refetchCategoryTabs();
        refetchBotCategories();
      },
    }
  );

  return mutation;
};

type PublicSourceType = {
  isWebsite: boolean;
  isNeedlWeb: boolean;
  isConnectPage: boolean;
  isCustomFeed: boolean;
};

type PublicSourceMutationPayload = {
  key: string;
  label?: string;
};

export {
  usePublicNavigationRefetch,
  usePublicSourceDelinkMutation,
  usePublicSourceCustomIntegrationMutation,
  useFollowWebsite,
  useUnfollowWebsite,
  usePublicSourceMultipleIntegrationMutation,
  usePublicSourceIntegrationMutation,
  useFeedTemplateMutation,
};
